import {
  PaginationCursorRespone,
  PaginationOffsetResponse,
  TableFilter,
} from '@fly/shared';
import { ColumnDef } from '@tanstack/react-table';
import {
  ColumnFiltersState,
  PaginationState,
  SortingState,
  Table,
} from '@tanstack/react-table';

export const CursorPaginationDirection = {
  PREV: 'prev',
  NEXT: 'next',
} as const;
export type CursorPaginationDirection =
  (typeof CursorPaginationDirection)[keyof typeof CursorPaginationDirection];

export type CursorPagination = {
  cursor_direction: CursorPaginationDirection;
  cursor: string;
};

export type CursorPaginationState = {
  cursor_direction: CursorPaginationDirection;
  cursor: string;
};

export type TableStateType = 'state' | 'search';
export type DataProcessing = 'client' | 'server';
export type PaginationType = 'none' | 'cursor' | 'offset' | 'virtual_scroll';

/** Default `data-testid` on TanstackTable virtual scroll viewport + matching custom renders (e.g. Releases timeline). Scope under a page layout testid when multiple tables exist. */
export const TANSTACK_TABLE_VIRTUAL_SCROLL_VIEWPORT_TEST_ID =
  'tanstack-table-scroll' as const;

/** Default fade gradient for `customRender` scroll viewports (matches dashboard ContentArea background). */
export const TANSTACK_TABLE_SCROLL_FADE_GRADIENT_COLOR =
  'var(--fly-color-scheme-gray-1)' as const;

/** Default fade height in px for `customRender` scroll viewports (issue #1421: 16–24px). */
export const TANSTACK_TABLE_SCROLL_FADE_GRADIENT_HEIGHT = 24;

export type TableState = {
  sorting: SortingState;
  columnFilters: ColumnFiltersState;
  pagination: PaginationState | CursorPaginationState | null;
  filters: TableFilter[];
};

export type StringKeysOf<T> = {
  [K in keyof T]: K extends string ? (T[K] extends string ? K : never) : never;
}[keyof T];

export type SerializedFilter = {
  key: string;
  value: string;
};

export type CustomRenderScrollFadeProps = {
  /** When false, custom render should use a plain scroll container (default true). */
  scrollFade?: boolean;
  scrollFadeGradientColor?: string;
  scrollFadeGradientHeight?: number;
};

export type CustomRender<T extends Record<string, any>> = React.ComponentType<
  {
    table: Table<T>;
    isDataEmpty: boolean;
    isFilterDataEmpty: boolean;
    loading: boolean;
    loadingMore?: boolean;
    nextCursor?: string | null;
    onLoadMore?: (cursor: string) => void;
    /** Virtual scroll viewport `data-testid` (must match TanstackTableVirtualView when using alternate scroll UI). */
    scrollViewportTestId?: string;
  } & CustomRenderScrollFadeProps
>;

/** Internal: grouped config built from flat props for validation and sub-components. */
export interface TanstackTableDataConfig<T> {
  stateType: TableStateType;
  dataProcessing: DataProcessing;
  paginationType: PaginationType;
  data: T[];
  columns: ColumnDef<T>[];
  serverPagination?: PaginationOffsetResponse | PaginationCursorRespone;
  searchKey: StringKeysOf<T>;
  paginationKey?: StringKeysOf<T>;
}

/** Internal: search/filter bar and layout. */
export interface TanstackTableControlsConfig {
  showSearchSeparator: boolean;
  fullSearchWidth: boolean;
  reverseSearchAndFilters: boolean;
  searchEndSlot?: React.ReactNode;
  searchStartSlot?: React.ReactNode;
  hideSearch: boolean;
  hideFilters: boolean;
  promptFilter?: React.ReactNode;
  enrichSearchControl?: React.ReactNode;
}

/** Internal: virtual scroll options (used when paginationType === 'virtual_scroll'). */
export interface TanstackTableVirtualScrollConfig {
  rowHeight?: number;
  loadMoreSkeletonRows?: number;
  onLoadMore?: (cursor: string) => void;
  loadingMore?: boolean;
}

/** Describes a single skeleton placeholder block. */
export type SkeletonElement = {
  width?: string;
  height?: string;
  className?: string;
};

/** Describes a horizontal line of skeleton blocks. */
export type SkeletonLine = {
  items: SkeletonElement[];
  className?: string;
};

/**
 * Declarative skeleton layout for a single column cell.
 * Each entry is either a bare array of elements (shorthand) or a SkeletonLine
 * with optional className for the line wrapper.
 *
 * @example
 * const config: SkeletonConfig = [
 *   [{ width: '16px', height: '16px' }, { width: '120px', className: 'rounded-full' }],
 *   { items: [{ width: '100px' }], className: 'opacity-50' },
 * ];
 */
export type SkeletonConfig = (SkeletonElement[] | SkeletonLine)[];

/** Internal: table body view (custom render, empty state, skeleton, layout). */
export interface TanstackTableViewConfig<T extends Record<string, any>> {
  customRender?: CustomRender<T>;
  hideTableFragmentIfEmpty: boolean;
  emptyStateElement?: React.ReactNode;
  skeletonRows: number;
  layout: 'fixed' | 'auto';
  scrollFade: boolean;
  scrollFadeGradientColor: string;
  scrollFadeGradientHeight: number;
}

/** Internal: cursor/offset pagination props passed to Pagination and getPaginationDisabled. */
export interface TanstackTablePaginationConfig<T = Record<string, unknown>> {
  paginationType: PaginationType;
  dataProcessing: DataProcessing;
  paginationKey?: StringKeysOf<T>;
  nextCursor?: string | null;
  prevCursor?: string | null;
}
